//  ----------------------------------------------------------------------------
// Dependencies
//  ----------------------------------------------------------------------------

import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { persistStore } from 'redux-persist';

import configureStore from 'store';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material/styles';
//  -- Global style -------------------
import GlobalStyles from 'GlobalStyles';
import config from 'config';
//  -- Routes -------------------------
import AllRoutes from 'containers';
import FirebaseProvider from 'containers/FirebaseContext';
import { AudioProvider } from 'utils/context/AudioProvider.tsx';
import InternetLostMessage from 'components/InternetLostMessage';
import { FullScreenProvider } from 'utils/context/FullScreenProvider';
import { SideDrawerProvider } from 'utils/context/SideDrawerProvider';
import { tokenRefreshManager } from 'utils/lib/tokenRefreshManager';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AlertLoadingProvider } from 'components/UI/alertSakeBar/alertProvider';
import { ConfirmProvider } from 'components/UI/confirmProvider';
import { SessionManager } from 'utils/lib/api_config';
import { THEME } from 'theme';
import GlobalEffects from 'components/GlobalEffects';
import { useSessionRefresher } from 'utils/hooks/sessionRefresh';
import { DndProvider, useDragLayer } from 'react-dnd';
import { TouchBackend } from 'react-dnd-touch-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { HowlAudioProvider } from 'components/hooks/useHowlProvider';
import InitialLoading from 'components/InitialLoading';
import styled from 'styled-components';
import { initApp } from 'utils/config/initApiConfig';
import { useAutoScroll } from 'components/hooks';
import { useOrientation } from 'components/hooks';
import { CustomDragPreview } from 'modules/dragPreview/CustomDragPreview';
import { useCurrentUser } from 'store/users/selectors';

//  -- AWS Configuration --------------
SessionManager();

const CenterContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  min-height: 90vh;
  justify-content: center;
  width: 100%;
`;


const store = configureStore();
const persistor = persistStore(store);
const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
const App = () => {
  const [mount, setMount] = React.useState(false);
  useSessionRefresher(15);
  useEffect(() => {
    try {
      const session_user = sessionStorage.getItem('username');
      if (session_user) {
        tokenRefreshManager.getValidSession()
          .then((session) => console.log('Session OK:', session.tokens?.idToken?.payload?.sub))
          .catch((err) => console.error('Session load failed:', err))
          .finally(() => {
            setMount(true);
          });
      } else {
        initApp();
        setTimeout(() => {
          setMount(true);
        }, 100);
      }

    } catch (error) {
      setMount(true);
     }
  }, []);
  if (!mount) {
    return <CenterContainer><InitialLoading mounted={true} transparent={false} /></CenterContainer>;
  }
  return (
    <BrowserRouter>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <GoogleOAuthProvider clientId={config.G_CLIENT_ID}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={THEME}>
                  <DndProvider backend={isTouch ? TouchBackend : HTML5Backend}>
                    <CustomDragPreview />
                    <FirebaseProvider>
                      <FullScreenProvider>
                        <SideDrawerProvider>
                          <AudioProvider>
                            <HowlAudioProvider>
                            <ConfirmProvider>
                              <AlertLoadingProvider>
                                <GlobalStyles />
                                <InternetLostMessage />
                                <GlobalEffects>
                                  <AllRoutes store={store} />
                                </GlobalEffects>
                              </AlertLoadingProvider>
                            </ConfirmProvider>
                            </HowlAudioProvider>
                          </AudioProvider>
                        </SideDrawerProvider>
                      </FullScreenProvider>
                    </FirebaseProvider>
                  </DndProvider>
                </ThemeProvider>
              </StyledEngineProvider>
            </LocalizationProvider>
          </GoogleOAuthProvider>
        </PersistGate>
      </Provider>
    </BrowserRouter>
  );
};

export default App;
